import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, ListItem, ListItemButton, Switch } from '@mui/material';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import { ALL, LONG_FULL_DATE_FORMAT, statusSubCampaign } from '@/helpers/constants';
import { getFullFormattedDate } from '@/helpers/utils';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import { UserSettings, Notification } from '@/types';
import styles from './NotificationsMenu.module.scss';
import { StateComp } from '../Notifications';

const useStyles = makeStyles((theme: Theme) => ({
  rootItem: {
    whiteSpace: 'normal',
    paddingTop: '6px',
    paddingBottom: '6px',
  },
  rootItemText: {
    maxWidth: '65%',
    paddingLeft: '30px',
  },
  rootItemTextButtons: {
    maxWidth: '65%',
    paddingRight: '45px',
  },
  secondaryItemText: {
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  rootItemIcon: {
    minWidth: theme.spacing(5),
  },
  rootItemSecondary: {
    maxWidth: '35%',
  },
  removeSingleCross: {
    fontSize: '0.8em',

  },
}));

interface NotificationsMenuProps {
  notifications: Notification[];
  displayAll: boolean;
  onDisplayAll: () => void;
  onPush: (id: string, campaignId: string, code: string) => void;
  isLoading: boolean;
  userSettings: UserSettings;
  hasOldNotifications: boolean;
  open: boolean;
  anchorEl: HTMLElement | null;
  state: StateComp;
  onClose: () => void;
  onMarkAllRead: () => void;
  changeSendNotifications: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  getNotificationStringByRole: (code: string, userCode: string, campaignId: string) => string;
  onDeleteNotification: (id) => void;
  setState?: React.Dispatch<React.SetStateAction<StateComp>>;
}

const NotificationsMenu = (props: NotificationsMenuProps) => {
  const { notifications = [], displayAll = false, onDisplayAll = () => null, onPush = () => null, isLoading = false,
    anchorEl, userSettings, hasOldNotifications, onMarkAllRead = () => null, getNotificationStringByRole = () => null,
    onDeleteNotification = () => null, changeSendNotifications, onClose } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { timezone, notificationAsEmail } = userSettings;
  const NEW_SUB_CR_CREATED_KEY = 'NEW_SUB_CR_CREATED';

  const handleOnClick = (id: string, campaignId: string, code: string) => {
    onPush(id, campaignId, code);
  };

  return (
    <Menu
      elevation={8}
      keepMounted
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{ style: { width: 650 } }}
    >
      <div>
        <div className={styles.NotificationsHeader}>
          <div className={styles.NotificationsTitle}>{t('notifications.sectionTitle')}</div>
          <div className={styles.Buttons}>
            <SimpleButton
              id="btn-clear-all-notifications"
              data-testid="btn-clear-all-notifications"
              color="primary"
              buttonClassName={styles.Button}
              buttonText={t('notifications.clearAllButton')}
              disabled={notifications.length === 0}
              handleClick={() => onDeleteNotification(ALL)}
            />
            <SimpleButton
              id="btn-read-all-notifications"
              data-testid="btn-read-all-notifications"
              color="primary"
              buttonClassName={styles.Button}
              buttonText={t('notifications.allRead')}
              disabled={notifications.length === 0 || notifications?.every(({ readByUser }) => readByUser)}
              handleClick={onMarkAllRead}
            />
          </div>
        </div>
        {isLoading && <div className={styles.LoadingContainer}><Spinner isVisible isMask small /></div>}
        {!isLoading
          && (
            <>
              <div className={styles.NotificationList}>
                {notifications.map((notification) => {
                  const { id, campaignId, creationDate, campaignStatus, readByUser, split } = notification;
                  const { code, userCode } = campaignStatus;
                  const isNewSubCRNotification = code === statusSubCampaign.IN_ANALYSIS && split;

                  return (
                    <ListItem classes={{ root: classes.rootItem }} data-testid={`notification-${id}`} key={id} onClick={() => handleOnClick(id, campaignId, code)} className={readByUser ? '' : styles.NotRead}>
                      <ListItemText
                        classes={{ root: classes.rootItemText, secondary: classes.secondaryItemText }}
                        primary={getNotificationStringByRole(
                          isNewSubCRNotification ? NEW_SUB_CR_CREATED_KEY : code,
                          userCode,
                          campaignId)
                        || ''}
                        secondary={campaignId}
                      />
                      <ListItemSecondaryAction classes={{ root: classes.rootItemSecondary }}>
                        {getFullFormattedDate(creationDate.split('[UTC]').join(''), timezone, LONG_FULL_DATE_FORMAT)}
                        <IconButton
                          data-testid="btn-remove-single-notification"
                          aria-label="close"
                          onClick={() => onDeleteNotification(id)}
                        >
                          <CloseIcon
                            className={classes.removeSingleCross}
                          />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
                {notifications.length === 0 && (
                  <div className={styles.NoNotifications}>{t('notifications.noNotifications')}</div>
                )}
              </div>
              <ListItem>
                <ListItemText classes={{ root: classes.rootItemTextButtons, secondary: classes.secondaryItemText }} primary={t('notifications.sendAllNotifications')} />
                <ListItemSecondaryAction>
                  <Switch
                    checked={!!notificationAsEmail}
                    onChange={changeSendNotifications}
                    id="switch-send-all-notifications"
                    data-testid="switch-send-all-notifications"
                    name="checkedB"
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
              {hasOldNotifications && (
                <ListItemButton onClick={onDisplayAll} data-testid="display-all-notifications">
                  <ListItemText classes={{ root: classes.rootItemTextButtons, secondary: classes.secondaryItemText }} primary={t('notifications.showMore')} />
                  {displayAll && (
                    <ListItemSecondaryAction>
                      <i className="di icon-check" />
                    </ListItemSecondaryAction>
                  )}
                </ListItemButton>
              )}
            </>
          )}
      </div>
    </Menu>
  );
};

const mapStateToProps = (state) => ({
  userSettings: getSettingsInfo(state),
});

export default connect(mapStateToProps)(NotificationsMenu);
